import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { DoneIcon } from '@estee/elc-icons';

import { Countdown } from '~components/Countdown';
import { FlashMessage } from '~components/FlashMessage';
import { Message } from '~components/Message';
import { OtpStatus, SENT_STATUS } from '~constants/Constants';
import { useTranslation } from '~utils/hooks/translations';

const LinkButton = styled.a`
    cursor: pointer;
`;

export interface IResendCodeProps {
    countdownSeconds?: number;
    enableCountdown?: boolean;
    resendCodeBlockThreshold: number;
    codeStatus: OtpStatus;
    onClick?(): void;
    needReset?: boolean;
    readonly?: boolean;
}

export const ResendCode = observer((props: IResendCodeProps) => {
    const {
        codeStatus,
        onClick,
        countdownSeconds = 0,
        enableCountdown = false,
        resendCodeBlockThreshold,
        needReset = false,
        readonly
    } = props;
    const { otpCodeSent, otpResendCode } = useTranslation();
    const [showCountdown, setShowCountdown] = React.useState(false);
    const [showResend, setShowResend] = React.useState(false);

    const resend = () => {
        if (readonly) {
            return;
        }
        onClick?.();
        setShowResend(false);
        setShowCountdown(false);
    };

    const onCodeSent = () => {
        if (enableCountdown) {
            setShowCountdown(true);
        } else {
            setShowResend(true);
        }
    };

    const onCountdownFinished = () => {
        setShowResend(true);
        setShowCountdown(false);
    };

    React.useEffect(() => {
        setShowResend(codeStatus !== SENT_STATUS);
    }, [codeStatus]);

    React.useEffect(() => {
        if (needReset) {
            setShowResend(false);
            setShowCountdown(false);
        }
    }, [needReset]);

    if (!showCountdown && showResend) {
        return <LinkButton onClick={resend}>{otpResendCode}</LinkButton>;
    }

    // TODO: APACRIGEL-1194 Need add translation key for 'Resend code in' later.
    return showCountdown ? (
        <>
            Resend code in
            <Countdown totalSeconds={countdownSeconds} onElapsed={onCountdownFinished} />
        </>
    ) : (
        <FlashMessage onElapsed={onCodeSent} duration={readonly ? 0 : resendCodeBlockThreshold}>
            <Message message={otpCodeSent} className="elc-iam-otp-code-sent">
                <DoneIcon className="elc-done-icon" />
            </Message>
        </FlashMessage>
    );
});
